import { useState, useEffect, useCallback } from "react";
import { Package, Plus, Users as UsersIcon, Trash2, Save, X, Search, Euro, MapPin, Calendar, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "./adminApi";

const EMPTY_FORM = {
  title: "",
  hourly_rate: "",
  description: "",
  profiles_needed: 1,
  agency_id: "",
  location: "",
  mission_start: "",
  mission_end: "",
  client_company: "",
  status: "open",
};

const STATUS_STYLE = {
  open: { label: "Ouverte", cls: "bg-[#F0E9F3] text-[#662483]" },
  filled: { label: "Pourvue", cls: "bg-green-100 text-green-700" },
  closed: { label: "Fermée", cls: "bg-gray-100 text-gray-500" },
};

export default function OrdersTab({ onNavigateToCandidates }) {
  const [orders, setOrders] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null); // enriched order (with attached_candidates_details)
  const [attachSearch, setAttachSearch] = useState("");
  const [attachResults, setAttachResults] = useState([]);
  const [attachSelectedIds, setAttachSelectedIds] = useState([]);
  const [attachComment, setAttachComment] = useState("");
  const [showAttach, setShowAttach] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/orders"); setOrders(r.data); }
    catch { toast.error("Impossible de charger les commandes"); }
    finally { setLoading(false); }
  }, []);
  const fetchAgencies = useCallback(async () => {
    try { const r = await api.get("/agencies"); setAgencies(r.data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchOrders(); fetchAgencies(); }, [fetchOrders, fetchAgencies]);

  // Search candidates for attach modal
  useEffect(() => {
    if (!showAttach) return;
    const t = setTimeout(async () => {
      try {
        const params = { role: "all", status: "active" };
        if (attachSearch) params.search = attachSearch;
        const r = await api.get("/candidates", { params });
        setAttachResults(r.data.slice(0, 50));
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [attachSearch, showAttach]);

  const submitCreate = async () => {
    if (!createForm.title.trim()) { toast.error("L'intitulé du poste est obligatoire"); return; }
    if (!createForm.hourly_rate || Number(createForm.hourly_rate) <= 0) { toast.error("Le taux horaire doit être supérieur à 0"); return; }
    setCreating(true);
    try {
      await api.post("/admin/orders", {
        ...createForm,
        hourly_rate: Number(createForm.hourly_rate),
        profiles_needed: Number(createForm.profiles_needed || 1),
      });
      toast.success("Commande créée");
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur à la création");
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (orderId) => {
    try {
      const r = await api.get(`/admin/orders/${orderId}`);
      setSelected(r.data);
    } catch { toast.error("Impossible de charger la commande"); }
  };

  const attachSelected = async () => {
    if (!selected || attachSelectedIds.length === 0) { toast.error("Sélectionnez au moins un candidat"); return; }
    try {
      const r = await api.post(`/admin/orders/${selected.order_id}/attach`, {
        user_ids: attachSelectedIds,
        comment: attachComment,
      });
      setSelected(r.data);
      setAttachSelectedIds([]);
      setAttachComment("");
      setShowAttach(false);
      fetchOrders();
      toast.success(`${attachSelectedIds.length} candidat(s) rattaché(s)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const detachCandidate = async (uid) => {
    try {
      const r = await api.delete(`/admin/orders/${selected.order_id}/candidates/${uid}`);
      setSelected(r.data);
      fetchOrders();
      toast.success("Candidat détaché");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const updateComment = async (uid, comment) => {
    try {
      const r = await api.patch(`/admin/orders/${selected.order_id}/candidates/${uid}`, { comment });
      setSelected(r.data);
      toast.success("Commentaire enregistré");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Supprimer définitivement cette commande ?")) return;
    try {
      await api.delete(`/admin/orders/${orderId}`);
      fetchOrders();
      if (selected?.order_id === orderId) setSelected(null);
      toast.success("Commande supprimée");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const agencyName = (id) => agencies.find((a) => a.agency_id === id)?.name || (id ? "?" : "—");

  return (
    <div data-testid="orders-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#3F3F3F] flex items-center gap-2">
            <Package className="w-5 h-5 text-[#662483]" /> Commandes clients
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Créez une commande, rattachez les profils du vivier et suivez l&apos;avancement.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white"
          data-testid="new-order-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nouvelle commande
        </Button>
      </div>

      {/* Orders list */}
      {loading ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 p-10 text-center" data-testid="orders-empty">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Aucune commande pour le moment. Créez la première.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 divide-y divide-gray-100" data-testid="orders-list">
          {orders.map((o) => {
            const style = STATUS_STYLE[o.status] || STATUS_STYLE.open;
            return (
              <div key={o.order_id} className="p-4 hover:bg-[#FAFAFA]" data-testid={`order-${o.order_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => openDetail(o.order_id)}
                        className="text-base font-bold text-[#3F3F3F] hover:text-[#662483] text-left"
                        data-testid={`order-open-${o.order_id}`}
                      >
                        {o.title}
                      </button>
                      <Badge className={`rounded-none text-[10px] font-bold uppercase ${style.cls}`}>{style.label}</Badge>
                      {o.client_company && <Badge className="rounded-none text-[10px] bg-blue-50 text-blue-700">{o.client_company}</Badge>}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-3">
                      <span className="flex items-center gap-1"><Euro className="w-3 h-3" /> {o.hourly_rate}€/h</span>
                      <span className="flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {o.attached_count}/{o.profiles_needed} profils</span>
                      {o.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.location}</span>}
                      {o.mission_start && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {o.mission_start}{o.mission_end ? ` → ${o.mission_end}` : ""}</span>}
                      <span className="text-gray-400">Agence : {agencyName(o.agency_id)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {o.attached_count > 0 && onNavigateToCandidates && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onNavigateToCandidates({ role: "all", status: "all", order_id: o.order_id })}
                        className="rounded-none text-xs h-8"
                        data-testid={`order-view-vivier-${o.order_id}`}
                      >
                        Voir dans le vivier
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openDetail(o.order_id)} className="rounded-none text-xs h-8" data-testid={`order-detail-${o.order_id}`}>
                      Détails
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteOrder(o.order_id)} className="rounded-none text-xs h-8 text-red-600 border-red-300 hover:bg-red-50" data-testid={`order-delete-${o.order_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {o.description && <p className="text-xs text-gray-600 mt-2 line-clamp-2">{o.description}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">Nouvelle commande</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Intitulé du poste *</Label>
              <Input value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} placeholder="ex : Cariste CACES 3" className="rounded-none" data-testid="new-order-title" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Taux horaire € *</Label>
              <Input type="number" step="0.01" min="0" value={createForm.hourly_rate} onChange={(e) => setCreateForm({ ...createForm, hourly_rate: e.target.value })} placeholder="12.50" className="rounded-none" data-testid="new-order-rate" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Nombre de profils</Label>
              <Input type="number" min="1" value={createForm.profiles_needed} onChange={(e) => setCreateForm({ ...createForm, profiles_needed: e.target.value })} className="rounded-none" data-testid="new-order-profiles" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Entreprise cliente</Label>
              <Input value={createForm.client_company} onChange={(e) => setCreateForm({ ...createForm, client_company: e.target.value })} placeholder="ex : DHL Marseille" className="rounded-none" data-testid="new-order-client" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Agence</Label>
              <Select value={createForm.agency_id || "__none__"} onValueChange={(v) => setCreateForm({ ...createForm, agency_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-none" data-testid="new-order-agency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sans agence —</SelectItem>
                  {agencies.map((a) => (
                    <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Lieu de la mission</Label>
              <Input value={createForm.location} onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })} placeholder="ex : Marseille (13)" className="rounded-none" data-testid="new-order-location" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Début</Label>
              <Input type="date" value={createForm.mission_start} onChange={(e) => setCreateForm({ ...createForm, mission_start: e.target.value })} className="rounded-none" data-testid="new-order-start" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Fin (prévue)</Label>
              <Input type="date" value={createForm.mission_end} onChange={(e) => setCreateForm({ ...createForm, mission_end: e.target.value })} className="rounded-none" data-testid="new-order-end" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold text-gray-700 mb-1 block">Descriptif de la mission</Label>
              <Textarea rows={4} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} placeholder="Tâches attendues, contraintes, horaires…" className="rounded-none" data-testid="new-order-description" />
            </div>
            <div className="col-span-2 flex gap-2 pt-2">
              <Button onClick={submitCreate} disabled={creating} className="rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white flex-1" data-testid="new-order-submit">
                <Save className="w-4 h-4 mr-2" /> {creating ? "Création..." : "Créer la commande"}
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#3F3F3F] flex items-center gap-2 flex-wrap">
                  <Package className="w-5 h-5 text-[#662483]" /> {selected.title}
                  <Badge className={`rounded-none text-[10px] font-bold uppercase ${STATUS_STYLE[selected.status]?.cls || ""}`}>{STATUS_STYLE[selected.status]?.label}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm mt-2 bg-[#FAFAFA] p-3 border border-gray-200">
                <p><span className="text-gray-500">Client :</span> {selected.client_company || "—"}</p>
                <p><span className="text-gray-500">Agence :</span> {agencyName(selected.agency_id)}</p>
                <p><span className="text-gray-500">Taux :</span> {selected.hourly_rate}€/h</p>
                <p><span className="text-gray-500">Lieu :</span> {selected.location || "—"}</p>
                <p><span className="text-gray-500">Début :</span> {selected.mission_start || "—"}</p>
                <p><span className="text-gray-500">Fin :</span> {selected.mission_end || "—"}</p>
                <p className="col-span-2"><span className="text-gray-500">Besoin :</span> {(selected.attached_candidates_details || []).length}/{selected.profiles_needed} profils</p>
                {selected.description && <p className="col-span-2 whitespace-pre-wrap text-gray-700"><span className="text-gray-500">Descriptif :</span> {selected.description}</p>}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <h4 className="text-sm font-bold text-[#3F3F3F]">Profils rattachés</h4>
                <Button size="sm" onClick={() => { setShowAttach(true); setAttachSelectedIds([]); setAttachComment(""); setAttachSearch(""); }} className="rounded-none bg-[#662483] hover:bg-[#552070] text-white" data-testid="attach-candidates-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter des profils
                </Button>
              </div>
              {(selected.attached_candidates_details || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic mt-2">Aucun candidat rattaché pour le moment.</p>
              ) : (
                <div className="mt-2 border border-gray-200 divide-y divide-gray-100" data-testid="attached-candidates-list">
                  {selected.attached_candidates_details.map((c) => (
                    <AttachedCandidateRow
                      key={c.user_id}
                      candidate={c}
                      onDetach={() => detachCandidate(c.user_id)}
                      onSaveComment={(comment) => updateComment(c.user_id, comment)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Attach modal */}
      <Dialog open={showAttach} onOpenChange={setShowAttach}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F]">Rattacher des candidats du vivier</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Rechercher nom, email, ville..." value={attachSearch} onChange={(e) => setAttachSearch(e.target.value)} className="rounded-none pl-10" data-testid="attach-search" />
            </div>
            <Textarea rows={2} placeholder="Commentaire commun aux profils rattachés (optionnel)" value={attachComment} onChange={(e) => setAttachComment(e.target.value)} className="rounded-none text-sm" data-testid="attach-comment" />
            <div className="border border-gray-200 max-h-96 overflow-y-auto divide-y divide-gray-100" data-testid="attach-results">
              {attachResults.length === 0 ? (
                <p className="text-xs text-gray-500 italic p-3">Aucun candidat trouvé.</p>
              ) : (
                attachResults.map((c) => {
                  const alreadyAttached = (selected?.attached_candidates_details || []).some((a) => a.user_id === c.user_id);
                  const checked = attachSelectedIds.includes(c.user_id);
                  return (
                    <label
                      key={c.user_id}
                      className={`flex items-center gap-3 p-2 text-sm ${alreadyAttached ? "opacity-40 cursor-not-allowed bg-gray-50" : "hover:bg-[#F0E9F3] cursor-pointer"}`}
                      data-testid={`attach-row-${c.user_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={alreadyAttached}
                        onChange={(e) => {
                          if (e.target.checked) setAttachSelectedIds((prev) => [...prev, c.user_id]);
                          else setAttachSelectedIds((prev) => prev.filter((id) => id !== c.user_id));
                        }}
                        className="accent-[#662483]"
                        data-testid={`attach-check-${c.user_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#3F3F3F] truncate">{c.name || c.email}</p>
                        <p className="text-[11px] text-gray-500 truncate">{c.email} · {c.phone || "—"} · {c.city || "—"}</p>
                      </div>
                      {alreadyAttached && <Badge className="rounded-none text-[10px] bg-gray-100 text-gray-500">Déjà attaché</Badge>}
                    </label>
                  );
                })
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={attachSelected} disabled={attachSelectedIds.length === 0} className="rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white flex-1" data-testid="attach-submit">
                <Plus className="w-4 h-4 mr-2" /> Rattacher {attachSelectedIds.length > 0 ? `(${attachSelectedIds.length})` : ""}
              </Button>
              <Button variant="outline" onClick={() => setShowAttach(false)} className="rounded-none">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AttachedCandidateRow({ candidate, onDetach, onSaveComment }) {
  const [comment, setComment] = useState(candidate.comment || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setComment(candidate.comment || ""); }, [candidate.comment]);
  const dirty = comment !== (candidate.comment || "");
  return (
    <div className="p-3 space-y-2" data-testid={`attached-row-${candidate.user_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#3F3F3F]">{candidate.name || candidate.email}</p>
          <p className="text-[11px] text-gray-500">{candidate.email} · {candidate.phone || "—"} · {candidate.city || "—"}</p>
          {candidate.role === "employee" && <Badge className="rounded-none text-[10px] bg-green-100 text-green-700 mt-1">Salarié</Badge>}
        </div>
        <Button variant="outline" size="sm" onClick={onDetach} className="rounded-none text-xs h-7 text-red-600 border-red-300 hover:bg-red-50" data-testid={`detach-${candidate.user_id}`}>
          <X className="w-3 h-3 mr-1" /> Détacher
        </Button>
      </div>
      <div className="flex items-start gap-2">
        <MessageCircle className="w-3.5 h-3.5 text-gray-400 mt-2 shrink-0" />
        <Textarea
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Commentaire admin (visible uniquement en interne)"
          className="rounded-none text-xs flex-1"
          data-testid={`comment-input-${candidate.user_id}`}
        />
        {dirty && (
          <Button
            size="sm"
            onClick={async () => { setSaving(true); try { await onSaveComment(comment); } finally { setSaving(false); } }}
            disabled={saving}
            className="rounded-none bg-[#662483] hover:bg-[#552070] text-white text-xs h-8"
            data-testid={`comment-save-${candidate.user_id}`}
          >
            <Save className="w-3 h-3 mr-1" /> Enregistrer
          </Button>
        )}
      </div>
    </div>
  );
}
